import { useEffect, useRef } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg, useDebouncedBenefitInput } from "@/features/benefit-workspace/useBenefitChildReads"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { IconLoader2, IconLock, IconUser, IconUserOff } from "@tabler/icons-react"
import { toast } from "sonner"
import { api, ApiError } from "@/lib/api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type { BenefitDetailDto, BenefitOwnershipDto } from "@/lib/benefit-types"
import { ownerDisplay } from "@/lib/benefit-ownership"
import { handledWorkspaceFailure } from "@/lib/benefit-save"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"

export type { BenefitStaffDirectoryEntry } from "@/features/benefit-workspace/benefitChildContracts"

/**
 * Shows the stable directory identity that owns a file and, for assignment-capable staff, provides
 * an explicit searchable owner picker. The browser sends only a directory UserId; names/emails are
 * display data and never become an authority or an assignment target.
 */
export function BenefitOwnershipCard({
  benefit,
  canAssign,
  onUpdated,
  onReload,
}: {
  benefit: BenefitDetailDto
  canAssign: boolean
  onUpdated: (ownership: BenefitOwnershipDto) => void
  onReload: (benefit: BenefitDetailDto) => void
}) {
  const ownership = benefit.ownership ?? { userId: null, displayName: null, email: null, version: 0 }
  const [editing, setEditing, editVersion] = useBenefitEdit(String(ownership.version), "ownership")
  const [search, setSearch] = useBenefitDraftState("ownership", "search", "")
  const [selectedUserId, setSelectedUserId] = useBenefitDraftState("ownership", "selectedUserId", ownership.userId)
  const [saving, setSaving] = useBenefitSaving()
  const review = useBenefitReviewActive()
  const changeButton = useRef<HTMLButtonElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const close = () => {
    if (!setEditing(false)) return
    setSearch("")
    // Resolve the remounted opener, not the button removed when editing started.
    // An unmounted card has null refs, so an old completion cannot steal focus.
    requestAnimationFrame(() => (changeButton.current ?? heading.current)?.focus())
  }
  const scope = useBenefitScopeArg(editing && canAssign)
  const settledSearch = useDebouncedBenefitInput(search.trim(), 200)
  const staffRead = benefitApi.useSearchBenefitStaffQuery(scope !== skipToken && settledSearch !== null
    ? { ...scope, search: settledSearch, limit: 100 } : skipToken)
  const loadingStaff = editing && canAssign && (settledSearch === null || staffRead.isFetching)
  const staffError = staffRead.isError ? "Unable to load CMS staff directory. Try another search or reopen the picker." : null
  const staff = !loadingStaff && !staffError && scope !== skipToken ? staffRead.currentData ?? [] : []

  useEffect(() => {
    // A changed owner is saved readback, not permission to rewrite an open choice/version.
    if (!editing) setSelectedUserId(ownership.userId)
  }, [ownership.userId, ownership.version, editing])

  const save = async () => {
    if (!canAssign || review || !editing || editVersion === null || saving) return
    setSaving(true)
    try {
      const updated = await api.patch<BenefitOwnershipDto>(
        `/api/benefitapplications/${benefit.id}/ownership`,
        { userId: selectedUserId, expectedVersion: Number(editVersion) },
      )
      onUpdated(updated)
      close()
      toast.success(selectedUserId === null ? "File left unassigned." : "File owner updated.")
    } catch (error) {
      if (handledWorkspaceFailure(benefit.id, error)) return
      if (error instanceof ApiError && error.status === 409) {
        try {
          const latest = await api.get<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}`)
          onReload(latest)
          close() // Preserve the standalone caller's explicit 409 reload/discard policy.
          toast.error("This file was assigned by someone else. The latest owner is shown.")
        } catch {
          toast.error("This file was assigned by someone else. Reload the page before retrying.")
        }
      } else {
        toast.error(error instanceof Error ? error.message : "Unable to update file owner.")
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            {ownership.userId ? <IconUser className="size-5" /> : <IconUserOff className="size-5" />}
            <div>
              <CardTitle className="text-base"><h3 ref={heading} tabIndex={-1}>Intake ownership</h3></CardTitle>
              <CardDescription>Explicit workload owner; status history and the legacy Staff label do not change it.</CardDescription>
            </div>
          </div>
          {canAssign && !editing && (
            <Button ref={changeButton} variant="outline" size="sm" onClick={() => setEditing(true)}>
              Change owner
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!editing ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="benefit-owner-readback">
            <Badge variant={ownership.userId ? "secondary" : "outline"}>
              {ownerDisplay(ownership)}
            </Badge>
            {ownership.email && <span className="text-sm text-muted-foreground">{ownership.email}</span>}
            {!canAssign && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><IconLock className="size-3" /> Assignment restricted</span>}
          </div>
        ) : (
          <div className="space-y-3" data-testid="benefit-owner-picker">
            <label htmlFor="benefit-owner-search" className="text-sm font-medium">Search active CMS staff</label>
            <Input
              id="benefit-owner-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Name or email"
              autoComplete="off"
              disabled={saving || review}
            />
            <div role="listbox" aria-label="Owner choices" className="max-h-56 overflow-y-auto rounded-md border p-1">
              <button
                type="button"
                role="option"
                aria-selected={selectedUserId === null}
                className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-muted ${selectedUserId === null ? "bg-muted" : ""}`}
                onClick={() => setSelectedUserId(null)}
                disabled={saving || review}
              >
                <IconUserOff className="size-4" />
                <span>Unassigned</span>
              </button>
              {loadingStaff ? (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground"><IconLoader2 className="size-4 animate-spin" /> Loading staff…</div>
              ) : staffError ? (
                <p role="alert" className="px-3 py-3 text-sm text-destructive">{staffError}</p>
              ) : staff.length === 0 ? (
                <p className="px-3 py-3 text-sm text-muted-foreground">No eligible active CMS staff match that search.</p>
              ) : staff.map(entry => (
                <button
                  key={entry.userId}
                  type="button"
                  role="option"
                  aria-selected={selectedUserId === entry.userId}
                  className={`flex w-full flex-col rounded px-3 py-2 text-left hover:bg-muted ${selectedUserId === entry.userId ? "bg-muted" : ""}`}
                  onClick={() => setSelectedUserId(entry.userId)}
                  disabled={saving || review}
                >
                  <span className="text-sm font-medium">{entry.displayName}</span>
                  <span className="text-xs text-muted-foreground">{entry.email}</span>
                </button>
              ))}
            </div>
            {!canAssign && <p role="status" className="text-sm text-muted-foreground">Assignment is currently unavailable. Your original choice and version are retained; cancel or refresh the saved data.</p>}
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={!canAssign || review || saving || selectedUserId === ownership.userId}>
                {saving && <IconLoader2 className="size-4 animate-spin" />}
                Save owner
              </Button>
              <Button variant="ghost" size="sm" onClick={close} disabled={saving || review}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
